import { v4 } from 'uuid';
import type { App } from '../../app/App';
import type { PiggyBankDependencies, PiggyBankObservable, PiggyBankTier } from '../piggyBank/types';
import { ObservableValue } from '../../app/state/createObservableValue';
import { TransactionReason, type ResourceMap } from '../../app/resources/types';
import { IAPPlacement } from '../../app/iap/types';
import type { AppStateController } from '../../app/state/AppStateController';
import { PIGGY_BANK_TIER_CONFIG_MAP } from '../piggyBank/PiggyBankConstants';
import { AdResponse } from '../../app/tracking/trackingModules/avo/Avo';

async function purchasePiggyBankIAP(
  app: App,
  tier: PiggyBankTier,
): Promise<{ success: boolean; reward?: ResourceMap }> {
  const { iap } = app;

  if (!iap) return { success: false };

  const productId = PIGGY_BANK_TIER_CONFIG_MAP[tier].iap;
  const offer = iap.getProduct(productId);
  if (!offer) return { success: false };

  const offerImpressionId = v4();

  const success = await iap.makePurchase({
    product: offer,
    placement: IAPPlacement.Shop,
    showResults: false,
    offerImpressionId,
  });
  if (!success) return { success: false };

  const reward = iap.getProduct(productId)?.rewards;
  return { success: true, reward };
}

async function watchAdForPiggyBank(app: App, tier: PiggyBankTier): Promise<{ success: boolean; reward?: ResourceMap }> {
  const { ads, resources } = app;
  if (!ads) return { success: false };

  const response = await ads.showRewardedAd('piggyBankAd');
  if (response !== AdResponse.SUCCESS) {
    return { success: false };
  }

  const reward: ResourceMap = {
    Coin: PIGGY_BANK_TIER_CONFIG_MAP[tier].maxCoins,
  };

  resources.addInAppPurchase(reward, TransactionReason.ShopPurchase, '');

  return { success: true, reward };
}

export function createPiggyBankObservable(stateController: AppStateController): PiggyBankObservable {
  return new ObservableValue(stateController, ['piggyBank', 'levelIndex', 'coins']);
}

export function createPiggyBankDependencies(app: App): PiggyBankDependencies {
  return {
    observable: createPiggyBankObservable(app.stateController),
    overlayContainer: app.controller.overlay.view.content,
    purchasePiggyBank: (tier: PiggyBankTier) => purchasePiggyBankIAP(app, tier),
    getPiggyBankPrice: async (tier: PiggyBankTier): Promise<string | null> => {
      const productId = PIGGY_BANK_TIER_CONFIG_MAP[tier].iap;
      const product = await app.iap?.getProductAsync?.(productId);
      if (!product) return null;
      return product.priceLabel;
    },
    watchAdForPiggyBank: app.iap === null ? (tier: PiggyBankTier) => watchAdForPiggyBank(app, tier) : undefined,
  };
}
